import React, { useState, useEffect, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';
import { Pointer, TailoredRegionPointer } from 'src/App/HOC';
import {
  applySmartBounds,
  calculateBounds,
  formatGoogleMapsBounds,
  getCoordinatesFromWKT,
  getDiscoveryMapViewParamsCountryWise,
  getBoundedClusters,
  getVisibleListings,
  isPointWithinBounds,
  mapOptions,
  calculatePercentiles,
  convertCurrency,
  applySmartBoundsForCoords,
  generateWKTPolygon,
  getBoundsFromCoordinates,
  getBoundedListings,
  getClusterListingsCoords,
  parseFilterValToApiResponse,
  isEmpty,
} from 'src/utils/functions';
import { Bounds, Coordinates, Listing, Listings, Submarket } from 'src/interfaces/idashboard';
import {
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  exchangeRates,
  HEATMAP_METRIC_TO_DATA_KEY_MAP,
  POINTER_GREEN,
} from 'src/const';
import {
  clusterListings,
  comparePolygonLimitsToBoundary,
  extractLatLong,
  getNaturalBreaks,
  getPolygonPaths,
  getSubmarketColor,
  sortListingBasedOnKey,
} from '../helper';
import { MapListingCountInfoBox } from 'src/App/HOC/Alerts/MapListingCountInfoBoxAlert';
import MapFilter from './MapFilter';
import MarketPointer from 'src/App/HOC/Pointer/MarketPointer';
import DiscoverCountryPointer from 'src/App/HOC/Pointer/DiscoverCountryPointer';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { useDispatch, useSelector } from 'react-redux';
import SearchBarModal from 'src/App/Components/Modals/SearchBarModal';
import * as turf from '@turf/turf';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { fetchVisibleMapSupply } from 'src/store/Dashboard/actions';
import SubmarketPointer from 'src/App/HOC/Pointer/SubmarketPointer';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  ClickAwayListener,
  Typography,
} from '@mui/material';
import DecisionDialog from 'src/App/Components/DecisionDialog';
import {
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_PRIMARY_2_300,
  COLOR_PRIMARY_2_500,
  COLOR_PRIMARY_2_700,
  NEIGHBOURHOOD_THEME,
} from 'src/colors';
import HeatmapLegendBox from '../UtilityComponents/HeatmapLegendBox';
import { ConfigSelect, DropdownSelect2 } from 'src/App/Components/Inputs';
import HeatmapPointer from 'src/App/HOC/Pointer/HeatmapPointer';
import ClusterPointer from 'src/App/HOC/Pointer/ClusterPointer';
import CottageIcon from '@mui/icons-material/Cottage';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import CollapsibleDrawer from './CollapsibleDrawer';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { ZoomButtons } from '../UtilityComponents/ZoomButtons';
import AppliedFiltersDisplay from '../UtilityComponents/AppliedFilterDisplay';
import SaveBoxPointer from 'src/App/HOC/Pointer/SaveBoxPointer';

const MAP_FILL_COLOR = POINTER_GREEN;
const CLUSTER_BREAK_ZOOM_LEVEL = 17;
const MIN_DRAWER_HEIGHT = 30; // 20% of viewport height
const MAX_DRAWER_HEIGHT = 60; // 60% of viewport height

const getLegendUnit = (neighbourhoodAnalysisKey, currency) => {
  if (
    neighbourhoodAnalysisKey == 'revenue' ||
    neighbourhoodAnalysisKey == 'revPar' ||
    neighbourhoodAnalysisKey == 'adr'
  ) {
    return [exchangeRates[currency].symbol, 'left'];
  } else if (
    neighbourhoodAnalysisKey == 'occupancy' ||
    neighbourhoodAnalysisKey == 'occupancyRate'
  ) {
    return ['%', 'right'];
  } else return ['', 'right'];
};

const formatLegendLabel = (min, max, unit, location) => {
  if (location === 'left') {
    return max !== undefined ? `${unit} ${min} - ${unit} ${max}` : `${unit} ${min}`;
  } else {
    return max !== undefined ? `${min}${unit} - ${max}${unit}` : `${min}${unit}`;
  }
};

const Map = ({
  marketID,
  matches,
  search,
  dashboardLayoutView,
  setDashboardLayoutView,
  listings,
  multipleBookingMarkersCallLoading,
  isDisabled,
  isUserAllowed,
  revPotentialRangeInvestment,
  currency,
  filteredListings,
  handleOnSampleClick,
  applyFilter,
  filterActive,
  setShowPremiumDataDynamic,
  toggleDrawer,
  setFilterVal,
  filterVal,
  setFilterActive,
  noDataInMarket,
  topPerformingListingOnFocus,
  setListingsGridStartIndexOffset,
  dummyMarketData,
  discoveryMarkets,
  setDiscoveryMarkets,
  topPerformingMarketOnFocus,
  setTopPerformingMarketOnFocus,
  marketsToCompareList,
  setMarketsToCompareList,
  minROI,
  maxROI,
  isMarketDataAccessible,
  freeMarketDataKey,
  isTabChanging,
  filteredDiscoveryMarkets,
  selectedDiscoveryCountry,
  setSelectedDiscoveryCountry,
  allRawMarketData,
  mapWidth,
  setIsDynamicDataBtnVisible,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  zoom,
  setZoom,
  bounds,
  setBounds,
  visibleMapListings,
  setVisibleMapListings,
  mapViewportListings,
  setMapViewportListings,
  setSubmarketPolygonPaths,
  mapLoading,
  setMapLoading,
  multipleMarkersCallLoading,
  OnBoardingSearchBox,
  showViewingLisingFromPaginationAlert,
  setShowViewingLisingFromPaginationAlert,
  mapHeightOnMobileView,
  setMapHeightOnMobileView,
  cachedMarketBedroomADR,
  cachedMarketData,
  callDynamicMarkers,
  submarkets,
  visibleSubmarkets,
  hoveredSubmarketId,
  setHoveredSubmarketId,
  handleHideSubmarket,
  parentMarketDetails,
  isDemoMarket,
  marketLevelActiveListings,
  savedClusters,
  setSavedClusters,
  shouldShowVerifiedSubmarkets,
  setShowConfirmFullMarkersFetchModal,
  getSubSectionHeading,
  getMapViewHeadings,
  OverViewMapSectionMarketStatsTooltip,
  isDynamicDataBtnVisible,
  SaveSubMarketButton,
  MainKpiMapView,
  isMapCollapsed,
  ListingCountHeader,
  ListingCardGrid,
  DynamicDataUpdateButton,
  marketDisplayName,
  activeListingCountDataToUse,
  currentHoveredCardFromDataGrid,
  setCurrentHoveredCardFromDataGrid,
  FilterBarComponent,
  showFilterComingSoonAlert,
  setShowFilterComingSoonAlert,
  filterCacheLoading,
  setFilterCacheLoading,
  warningTextComponent,
  filterApplied,
  listingSortingKey
}) => {
  const user = useSelector(state => (state as any).dashboard.user);
  const useFullMarkers = useSelector(state => (state as any).dashboard.useFullMarkers);
  const backendComputedClusters = useSelector(state => (state as any).dashboard.clusters);

  const dispatch = useDispatch();
  const {
    polygon_to_use,
    isPolyToUseMultiPolygon,
    center_to_use,
    fixed_center_in_mobile_view,
    discovery_default_zoom_level,
    redirect_to_us,
  } = getDiscoveryMapViewParamsCountryWise(selectedDiscoveryCountry);

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: search.data.lat,
    lng: search.data.lng,
  });
  const [tooltipPosition, setTooltipPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [hoveredSubmarketData, setHoveredSubmarketData] = useState<any>(null);
  const [hoveredClusterData, setHoveredClusterData] = useState<any>(null);
  const [hoveredClusterId, setHoveredClusterId] = useState<any>(null);
  const [hoveredClusterPolygon, setHoveredClusterPolygon] = useState<any>(null);
  const [clickedClusterId, setClickedClusterId] = useState<any>(null);
  const [rightClickedCluster, setRightClickedCluster] = useState<any>(null);
  const [discoveryMarkers, setDiscoveryMarkers] = useState<(JSX.Element | undefined)[] | null>(
    null
  );
  const [clusterListingsCoords, setClusterListingsCoords] = useState<
    {
      latitude: number;
      longitude: number;
    }[]
  >([]);

  const [mapCopy, setMapCopy] = useState<any>(null);
  const [mapsCopy, setMapsCopy] = useState<any>(null);
  const [searchPolygonCopy, setSearchPolygonCopy] = useState<any>(null);
  const [discoveryPolygonCopy, setDiscoveryPolygonCopy] = useState<any>(null);
  const [polygonPath, setPolygonPath] = useState<any>(null);

  const [polygonCopiesDiscovery, setPolygonCopiesDiscovery] = useState<any | null>(null);
  const [discoveryMapBounds, setDiscoveryMapBounds] = useState<GoogleMapReact.Bounds | null>(null);
  const [notSelectedDiscoveryCountryPolygons, setNotSelectedDiscoveryCountryPolygons] = useState<
    any[]
  >([]);
  const [notSelectedDiscoveryCountryPointer, setNotSelectedDiscoveryCountryPointer] = useState<
    JSX.Element[]
  >([]);
  const [neighbourhoodAnalysisKey, setNeighbourhoodAnalysisKey] = useState<string>('listing');
  const [neighbourhoodChoroplethLegends, setNeighbourhoodChoroplethLegends] = useState<any>({});
  const [clusterChoroplethLegends, setClusterChoroplethLegends] = useState<any>({});

  const isMarketTab: boolean =
    dashboardLayoutView !== 'discovery' &&
    dashboardLayoutView !== 'compare' &&
    dashboardLayoutView !== 'submarkets';

  const [polygonBounds, setPolygonBounds] = useState<{
    maxLat: number;
    minLat: number;
    maxLng: number;
    minLng: number;
  } | null>(null);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState<
    'open' | 'alreadyOpened' | 'neverOpened'
  >('neverOpened');

  const [submarketPolygons, setSubmarketPolygons] = useState<any[]>([]);
  const [infoWindow, setInfoWindow] = useState<any>(null);

  const [selectedSubmarket, setSelectedSubmarket] = useState<any>(null);
  const [hideConfirmOpen, setHideConfirmOpen] = useState(false);
  const [shouldShowSubmarketMarkers, setShouldShowSubmarketMarkers] = useState(false);
  const [clusters, setClusters] = useState<any[]>([]);
  const [clustersLoading, setClustersLoading] = useState(true);
  const [visibleMapClusters, setVisibleMapClusters] = useState<any[]>([]);
  const [shouldShowClusters, setShouldShowClusters] = useState(true);
  const [mapMode, setMapMode] = useState<'clusters' | 'heatmap' | 'listings'>('listings');
  const [clusterKey, setClusterKey] = useState<string>('supply');
  const [heatmapMode, setHeatmapMode] = useState<'smartZones' | 'submarkets'>('smartZones');
  const [hasClickedCluster, setHasClickedCluster] = useState(false);
  const [lastZoomLevel, setLastZoomLevel] = useState<number | null>(null);

  const [drawerHeight, setDrawerHeight] = useState(MIN_DRAWER_HEIGHT);
  const [collapsed, setCollapsed] = useState(true);

  const heatmapModeOptions = {
    smartZones: 'Smart Zones',
    submarkets: 'Submarkets',
  };

  const clusterMetricOptions = {
    supply: 'Listing Count',
    occupancyRate: 'Occupancy Rate',
    adr: 'ADR',
    revPar: 'RevPAR',
  };

  const isClusterHeatmapActive = useMemo(() => {
    return clusters.length > 0;
  }, [clusters]);

  useEffect(() => {
    if (!isClusterHeatmapActive) {
      setMapMode('listings');
    } else {
      setMapMode(mapMode);
    }
  }, [isClusterHeatmapActive, mapMode]);

  // toggles map center from different tabs
  useEffect(() => {
    if (!isMarketTab) {
      matches ? setMapCenter(fixed_center_in_mobile_view) : setMapCenter(center_to_use);
    } else {
      setTopPerformingMarketOnFocus(_ => null);
      setMapCenter({ lat: search.data.lat, lng: search.data.lng });
    }
  }, [dashboardLayoutView, selectedDiscoveryCountry]);

  // Explore markets of other country
  useEffect(() => {
    if (notSelectedDiscoveryCountryPolygons.length) {
      notSelectedDiscoveryCountryPolygons.forEach(polygon => {
        polygon.setMap(null);
      });
      setNotSelectedDiscoveryCountryPolygons([]);
    }
    setNotSelectedDiscoveryCountryPointer([]);

    if (selectedDiscoveryCountry == '') return;

    const tempNonSelectedMarketPolygonsArray: any[] = [];
    const tempNonSelectedMarketPointer: JSX.Element[] = [];

    ['US', 'GB', 'AU', 'CA'].forEach(countryCode => {
      if (selectedDiscoveryCountry !== countryCode) {
        const currentCountryGeometry = getDiscoveryMapViewParamsCountryWise(countryCode);
        const currentCountryPolygon = currentCountryGeometry.polygon_to_use;
        const curentCountryCenter = currentCountryGeometry.center_to_use;
        const isPolyToUseMultiPolygon = currentCountryGeometry.isPolyToUseMultiPolygon;

        if (
          mapCopy &&
          mapsCopy &&
          (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare')
        ) {
          const paths = getPolygonPaths(
            isPolyToUseMultiPolygon ? '' : currentCountryPolygon,
            isPolyToUseMultiPolygon ? currentCountryPolygon : ''
          );
          const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
          tempNonSelectedMarketPolygonsArray.push(polygon);
          tempNonSelectedMarketPointer.push(
            <DiscoverCountryPointer
              lat={curentCountryCenter.lat}
              lng={curentCountryCenter.lng}
              countryCode={countryCode as string}
              handleClick={() => {
                setSelectedDiscoveryCountry(countryCode as string);
                window.history.pushState('', '', '/markets/' + countryCode);
              }}
            />
          );
        }
      }
    });

    tempNonSelectedMarketPolygonsArray.length > 0 &&
      setNotSelectedDiscoveryCountryPolygons(tempNonSelectedMarketPolygonsArray);
    tempNonSelectedMarketPointer.length > 0 &&
      setNotSelectedDiscoveryCountryPointer(tempNonSelectedMarketPointer);
  }, [dashboardLayoutView, selectedDiscoveryCountry, mapCopy, mapsCopy]);

  useEffect(() => {
    if (!search.loading && bounds) {
      let newClusters: any[] = [];
      if (!backendComputedClusters.loading && backendComputedClusters.data.length > 0) {
        newClusters = backendComputedClusters.data;
        setClusters(newClusters);
        setVisibleMapClusters(newClusters);
        setClustersLoading(false);
      } else if (useFullMarkers && !multipleMarkersCallLoading && !listings.loading) {
        newClusters = clusterListings(listings as any, search);
        setClusters(newClusters);
        setVisibleMapClusters(newClusters);
        setClustersLoading(false);
      }
    }
  }, [
    useFullMarkers,
    search,
    listings,
    multipleMarkersCallLoading,
    filterActive,
    filteredListings,
    bounds,
    backendComputedClusters,
  ]);

  const FilterIsNotApplicableText = <></>;

  const getColors = (percentiles, value, isFaded = false) => {
    // > 75th percentile: COLOR_PRIMARY_2
    // 50th - 75th percentile: COLOR_PRIMARY_2_600
    // 25th - 50th percentile: COLOR_PRIMARY_2_400
    // < 25th percentile: COLOR_PRIMARY_2_200

    if (isFaded) {
      return COLOR_PRIMARY_2_100;
    }

    if (value === null) {
      return COLOR_PRIMARY_2_100;
    }

    if (value > percentiles[2]) {
      return COLOR_PRIMARY_2;
    } else if (value > percentiles[1]) {
      return COLOR_PRIMARY_2_700;
    } else if (value > percentiles[0]) {
      return COLOR_PRIMARY_2_500;
    } else {
      return COLOR_PRIMARY_2_300;
    }
  };

  const clusterMarkers = useMemo(() => {
    if (!clusters || !bounds || Object.keys(bounds).length === 0) return null;

    // Calculate percentiles for coloring
    const dataArray = clusters.map(cluster => cluster[clusterKey]);
    const [percentile25, percentile50, percentile75] = calculatePercentiles(dataArray, [
      0.25,
      0.5,
      0.75,
    ]);

    const [unit, location] = getLegendUnit(clusterKey, currency);

    let breaks = [percentile25, percentile50, percentile75];
    if (clusterKey == 'adr' || clusterKey == 'revPar') {
      breaks = [
        convertCurrency(currency, percentile25),
        convertCurrency(currency, percentile50),
        convertCurrency(currency, percentile75),
      ];
    }
    const legends = {
      [`< ${formatLegendLabel(breaks[0], undefined, unit, location)}`]: COLOR_PRIMARY_2_300,
      [`${formatLegendLabel(breaks[0], breaks[1], unit, location)}`]: COLOR_PRIMARY_2_500,
      [`${formatLegendLabel(breaks[1], breaks[2], unit, location)}`]: COLOR_PRIMARY_2_700,
      [`> ${formatLegendLabel(breaks[2], undefined, unit, location)}`]: COLOR_PRIMARY_2,
    };
    setClusterChoroplethLegends(legends);

    const boundedClusters = getBoundedClusters(clusters, bounds);

    if (boundedClusters.length === 0) return null;

    const gridSize = 5;
    const minClustersPerCell = 1;
    const maxClustersToShow = matches ? 10 : 30;
    const numberOfHiddenClustersToShow = 10;

    // Create a n x n grid
    const latStep = (bounds.northEast.lat - bounds.southWest.lat) / gridSize;
    const lngStep = (bounds.northEast.lng - bounds.southWest.lng) / gridSize;

    // Initialize grid cells
    const grid: any[][] = Array(gridSize)
      .fill(null)
      .map(() =>
        Array(gridSize)
          .fill(null)
          .map(() => [])
      );

    // Sort clusters by performance
    const sortedClusters = boundedClusters.sort((a, b) => {
      return b[clusterKey] - a[clusterKey];
    });

    // Assign clusters to grid cells
    sortedClusters.forEach(cluster => {
      const centroid = cluster.centroid;
      const latIndex = Math.min(
        Math.floor((centroid.latitude - bounds.southWest.lat) / latStep),
        gridSize - 1
      );
      const lngIndex = Math.min(
        Math.floor((centroid.longitude - bounds.southWest.lng) / lngStep),
        gridSize - 1
      );
      grid[latIndex][lngIndex].push(cluster);
    });

    // Select top clusters from each grid cell
    const selectedClusters: any[] = [];

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const cellClusters = grid[i][j];
        if (cellClusters.length > 0) {
          // Take top clusters from this cell
          selectedClusters.push(...cellClusters.slice(0, minClustersPerCell));
        }
      }
    }

    // If we have fewer clusters than needed, add more from the sorted list
    if (selectedClusters.length < maxClustersToShow) {
      const remainingClusters = sortedClusters.filter(
        cluster => !selectedClusters.includes(cluster)
      );
      selectedClusters.push(
        ...remainingClusters.slice(
          0,
          maxClustersToShow + numberOfHiddenClustersToShow - selectedClusters.length
        )
      );
    }

    return selectedClusters.map((cluster, index) => {
      const centroid = cluster.centroid;
      return (
        <ClusterPointer
          marketID={marketID}
          key={cluster.cluster_id}
          lat={centroid.latitude}
          lng={centroid.longitude}
          supply={cluster.supply}
          performanceMetrics={{
            adr: cluster.adr,
            occupancyRate: cluster.occupancyRate,
            revPar: cluster.revPar,
            supply: cluster.supply,
          }}
          clusterKey={clusterKey}
          currency={currency}
          color={getColors(
            [percentile25, percentile50, percentile75],
            cluster[clusterKey],
            index >= maxClustersToShow
          )}
          shouldFade={index >= maxClustersToShow}
          savedClusters={savedClusters}
          setSavedClusters={setSavedClusters}
        />
      );
    });
  }, [clusters, clusterKey, currency, matches, bounds, mapCopy, mapsCopy, savedClusters]);

  useEffect(() => {
    if (hoveredClusterId) {
      const hoveredCluster = clusters.find(cluster => cluster.cluster_id === hoveredClusterId);
      hoveredCluster && !matches && handleClusterHover(hoveredCluster);
    } else {
      handleClusterHoverOut();
    }
  }, [hoveredClusterId, clusters, matches]);

  const discoverMarketChip = (visibleDiscoveryMarkets: any = null) => {
    let clusteredDiscoveryMarkers: any[] = [];
    setDiscoveryMarkers(null);
    if (discoveryMarkets || visibleDiscoveryMarkets) {
      clusteredDiscoveryMarkers = (visibleDiscoveryMarkets
        ? visibleDiscoveryMarkets
        : discoveryMarkets
      )
        .filter(marketData => {
          return filteredDiscoveryMarkets.has(
            marketData.district + marketData.city + marketData.countryCode
          );
        })
        .slice(0, 10)
        .reverse()
        .map(marketData => {
          const [longitude, latitude]: [number, number] = marketData.center
            .slice(6, marketData.center.length - 1)
            .split(' ')
            .map(Number);

          return (
            <MarketPointer
              key={`listing-cluster-${marketData.district}-${
                marketData.ten_years_roi
              }-${Math.random()}`}
              id={marketData.district}
              lat={latitude}
              lng={longitude}
              countryCode={marketData.country_code}
              pointSize={20}
              name={marketData.district as string}
              score={marketData.ten_years_roi}
              data={marketData}
              isHighlighted={
                topPerformingMarketOnFocus &&
                topPerformingMarketOnFocus.district === marketData.district
              }
              marketsToCompareList={marketsToCompareList}
              setMarketsToCompareList={setMarketsToCompareList}
              dashboardLayoutView={dashboardLayoutView}
              minROI={minROI}
              maxROI={maxROI}
              isMarketDataAccessible={isMarketDataAccessible}
              currency={currency}
              freeMarketDataKey={freeMarketDataKey}
              handleOnSampleClick={handleOnSampleClick}
              marketLength={allRawMarketData ? allRawMarketData.length : 0}
            />
          );
        });

      setDiscoveryMarkers(clusteredDiscoveryMarkers);
    }
  };

  useEffect(() => {
    if (topPerformingMarketOnFocus) {
      let center = topPerformingMarketOnFocus.center;
      const { latitude, longitude } = extractLatLong(center);
      setMapCenter({ lat: latitude, lng: longitude });
    }
  }, [topPerformingMarketOnFocus]);

  useEffect(() => {
    discoveryMapBounds
      ? getVisibleDiscoveryPolygons(
          discoveryMapBounds as GoogleMapReact.Bounds,
          topPerformingMarketOnFocus,
          dummyMarketData,
          marketsToCompareList
        )
      : discoverMarketChip();
  }, [
    filteredDiscoveryMarkets,
    dashboardLayoutView,
    marketsToCompareList,
    selectedDiscoveryCountry,
    dummyMarketData,
  ]);

  const SearchBarModalComponent = (
    <SearchBarModal
      open={isSearchModalOpen}
      setOpen={setIsSearchModalOpen}
      OnBoardingSearchBox={() => OnBoardingSearchBox('map')}
    />
  );

  const getVisibleDiscoveryPolygons = (
    bounds: GoogleMapReact.Bounds,
    topPerformingMarketOnFocus: any = null,
    dummyMarketData: any[] = [],
    marketsToCompareList: any[] = []
  ) => {
    const visibleDiscoveryMarkets: any[] = [];
    if (topPerformingMarketOnFocus) {
      visibleDiscoveryMarkets.push(topPerformingMarketOnFocus);
    }

    if (marketsToCompareList) {
      for (let data of marketsToCompareList) {
        dummyMarketData &&
          dummyMarketData.forEach(market => {
            if (data[0] === market.district) {
              visibleDiscoveryMarkets.push(market);
            }
          });
      }
    }

    dummyMarketData &&
      dummyMarketData.forEach(market => {
        const isThisMarketCenterVisible = isPointWithinBounds(market.center, bounds);
        if (isThisMarketCenterVisible) {
          visibleDiscoveryMarkets.push(market);
        }
      });

    discoverMarketChip(visibleDiscoveryMarkets);
    setDiscoveryMarkets(mapWidth === 0 ? visibleDiscoveryMarkets : dummyMarketData);

    return visibleDiscoveryMarkets;
  };

  const alertIfUserOutOfRange = bounds => {
    try {
      if (isSearchModalOpen === 'alreadyOpened' || !polygonPath) return;
      // Convert map bounds to a Turf.js polygon
      const mapViewPolygon = turf.bboxPolygon([
        bounds.sw.lng,
        bounds.sw.lat, // southwest corner (lng, lat)
        bounds.ne.lng,
        bounds.ne.lat, // northeast corner (lng, lat)
      ]);
      // Convert your polygon array to a Turf.js polygon
      const turfPolygon = turf.polygon([polygonPath[0].map(point => [point.lng, point.lat])]);
      // Check if the polygon intersects with the map bounds
      const isPolygonInView = turf.intersect(turfPolygon, mapViewPolygon) !== null;

      if (!isPolygonInView) {
        setIsSearchModalOpen('open');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateMarketDetailsPremiumDataVisibility = (bounds: Bounds, isUserAllowed: boolean) => {
    const { isInside: isPolygonInsideOfBounds } = comparePolygonLimitsToBoundary(
      polygonBounds,
      bounds,
      20
    );

    if (!isUserAllowed && !isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(false);
    } else if (isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(true);
    }
  };

  const handleMapChange = async ({ center, bounds, zoom }: GoogleMapReact.ChangeEventValue) => {
    if (isTabChanging) {
      return;
    }

    if (isMarketTab) {
      alertIfUserOutOfRange(bounds);
      setMapLoading(true);
      showViewingLisingFromPaginationAlert === 'showing' &&
        setShowViewingLisingFromPaginationAlert('shown');

      visibleMapListings && visibleMapListings.length > 0 && setIsDynamicDataBtnVisible(true);
      const boundsObj = formatGoogleMapsBounds(bounds);
      setBounds(boundsObj);
      setZoom(zoom);

      const { filters } = filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
        ? { filters: {} }
        : parseFilterValToApiResponse(filterVal);
      dispatch(
        fetchVisibleMapSupply(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          boundsObj,
          search.data.isSubmarket ? {} : filters,
          listingSortingKey
        )
      );

      const polygonArray: [number, number][] = [
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Top-right
        [boundsObj.southWest.lat, boundsObj.northEast.lng], // Bottom-right
        [boundsObj.southWest.lat, boundsObj.southWest.lng], // Bottom-left
        [boundsObj.northEast.lat, boundsObj.southWest.lng], // Top-left
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Close the polygon (back to top-right)
      ];
      setSubmarketPolygonPaths(polygonArray);
      
      await callDynamicMarkers(marketID, boundsObj, filterVal);

      setListingsGridStartIndexOffset(_ => 0);
      polygonBounds && updateMarketDetailsPremiumDataVisibility(boundsObj, isUserAllowed);

      setTimeout(() => {
        setMapLoading(false);
      }, 500);
    } else {
      setDiscoveryMapBounds(bounds);

      getVisibleDiscoveryPolygons(
        bounds,
        topPerformingMarketOnFocus,
        dummyMarketData,
        marketsToCompareList
      );
    }
  };

  const isBoundLoaded = !isEmpty(bounds);

  useEffect(() => {    
    if (isBoundLoaded) {
      isBoundLoaded && callDynamicMarkers(marketID, bounds, filterVal);
      // const {filters} = filterVal && !showFilterComingSoonAlert ? parseFilterValToApiResponse(filterVal) : {filters: {}};
      const {filters} = filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed ? {filters: {}} : parseFilterValToApiResponse(filterVal);
      dispatch(fetchVisibleMapSupply(
        marketID,
        search.data.polygon || search.data.multiPolygon,
        bounds,
        search.data.isSubmarket ? {} : filters,
        listingSortingKey
      ))
    }
  }, [filterVal, isBoundLoaded]);

  const setPolygon = (
    map,
    maps,
    paths: Coordinates[][],
    strokeColor: string,
    fillColor: string,
    strokeOpacity = 0.8,
    strokeWeight = 2,
    fillOpacity = 0.12,
    submarketId?: string,
    submarketData?: any,
    zIndex = 1
  ) => {
    const polygon = new maps.Polygon({
      paths: paths,
      strokeColor: strokeColor,
      strokeOpacity: strokeOpacity,
      strokeWeight: strokeWeight,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      zIndex: zIndex,
    });
    polygon.setMap(map);

    if (submarketId) {
      maps.event.addListener(polygon, 'mouseover', e => {
        polygon.setOptions({
          fillOpacity: 0.4,
          strokeWeight: 3,
          zIndex: 10,
        });
        setHoveredSubmarketId(submarketId);
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        setHoveredSubmarketData(submarketData);
      });

      maps.event.addListener(polygon, 'mousemove', e => {
        setTooltipPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });

      maps.event.addListener(polygon, 'mouseout', () => {
        polygon.setOptions({
          fillOpacity: fillOpacity,
          strokeWeight: strokeWeight,
          zIndex: zIndex,
        });
        setHoveredSubmarketId(null);
        setTooltipPosition(null);
        setHoveredSubmarketData(null);
      });

      maps.event.addListener(polygon, 'click', () => {
        setSelectedSubmarket(submarketData);
        setHideConfirmOpen(true);
      });
    }

    if (isMarketTab) {
      // set dashboard to map view on interaction with map/polygons
      maps.event.addListener(polygon, 'click', function(e) {
        setDashboardLayoutView(e => {
          if (e == 'dashboard') {
            return 'map';
          }
          return e;
        });
      });

      maps.event.addListener(map, 'dragend', function(e) {
        setDashboardLayoutView(e => {
          if (e == 'dashboard') {
            return 'map';
          }
          return e;
        });
      });
    }

    return polygon;
  };

  const apiIsLoaded = (map, maps, polygonRaw: string, multiPolygonRaw: string) => {
    if (isMarketTab) {
      polygonCopiesDiscovery &&
        Object.keys(polygonCopiesDiscovery).forEach(polygonID => {
          polygonCopiesDiscovery[polygonID].setMap(null);
        });

      const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
      setPolygonPath(paths);
      const polygon = setPolygon(
        map,
        maps,
        paths,
        COLOR_PRIMARY_DARK,
        MAP_FILL_COLOR,
        0.8,
        2,
        0.12,
        undefined,
        undefined,
        1
      );
      applySmartBounds(map, maps, {} as Bounds, [polygon]);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
      callDynamicMarkers(marketID, bounds, filterVal);
    }

    setMapCopy(map);
    setMapsCopy(maps);
  };

  useEffect(() => {
    if (isMarketTab && mapCopy && mapsCopy && search.data) {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
      searchPolygonCopy && searchPolygonCopy.setMap(null);
      polygonCopiesDiscovery &&
        Object.keys(polygonCopiesDiscovery).forEach(polygonID => {
          polygonCopiesDiscovery[polygonID].setMap(null);
        });
      setPolygonCopiesDiscovery(null);

      setMapCenter({ lat: search.data.lat, lng: search.data.lng });

      const paths = getPolygonPaths(search.data.polygon, search.data.multiPolygon);
      const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      applySmartBounds(mapCopy, mapsCopy, {} as Bounds, [polygon]);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
    } else {
      searchPolygonCopy && searchPolygonCopy.setMap(null);
      !isMarketTab && setZoom(discovery_default_zoom_level);
    }
  }, [
    dashboardLayoutView,
    marketsToCompareList,
    selectedDiscoveryCountry,
    filteredDiscoveryMarkets,
  ]);

  useEffect(() => {
    if (
      mapCopy &&
      mapsCopy &&
      (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare')
    ) {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
      const paths = getPolygonPaths(
        isPolyToUseMultiPolygon ? '' : polygon_to_use,
        isPolyToUseMultiPolygon ? polygon_to_use : ''
      );
      const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      setDiscoveryPolygonCopy(polygon);
    } else {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
    }
  }, [mapCopy, mapsCopy, dashboardLayoutView, selectedDiscoveryCountry]);

  useEffect(() => {
    if (mapsCopy) {
      setInfoWindow(new mapsCopy.InfoWindow());
    }
  }, [mapsCopy]);

  const markers = useMemo(() => {
    if (visibleMapListings && visibleMapListings.length) {
      let revenueMarkerCount = 0;
      return visibleMapListings.map((listing: Listing, index: number) => {
        const item = listing;
        if (index % 2 == 0) {
          revenueMarkerCount = revenueMarkerCount + 1;
        }
        const bedroom = listing?.bedrooms?.toLowerCase() == 'studio' ? 0 : listing?.bedrooms;
        const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;

        return (
          <Pointer
            key={index + '-map-pointer'}
            lat={item.latitude}
            lng={item.longitude}
            setSubscriptionModalOpen={setSubscriptionModalOpen}
            data={item}
            canDelete={true}
            isDisabled={isDisabled}
            currency={currency}
            subscriptionModalOpen={subscriptionModalOpen}
            multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
            market={search.data.name}
            handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
            user={user}
            topPerformingListingOnFocus={topPerformingListingOnFocus}
            isEstimator={false}
            openDemoBookingForm={openDemoBookingForm}
            setDemoFormHeadingFor={setDemoFormHeadingFor}
            showRevenuePotential={index % 2 == 0 && revenueMarkerCount <= 10 ? true : false}
            referenceADR={referenceADR}
            cachedMarketData={cachedMarketData}
            dataKey={clusterKey == 'supply' ? 'adr' : clusterKey}
            currentHoveredCardFromDataGrid={currentHoveredCardFromDataGrid}
          />
        );
      });
    }
    return <></>;
  }, [
    visibleMapListings,
    currency,
    topPerformingListingOnFocus,
    clusterKey,
    currentHoveredCardFromDataGrid,
  ]);

  const hoveredClusterMarkers = useMemo(() => {
    if (clusterListingsCoords) {
      return clusterListingsCoords.map(
        (listing: { latitude: number; longitude: number }, index: number) => {
          return (
            <TailoredRegionPointer
              key={index + '-map-pointer'}
              lat={listing.latitude}
              lng={listing.longitude}
            />
          );
        }
      );
    }
    return <></>;
  }, [clusterListingsCoords]);

  useEffect(() => {
    if (
      dashboardLayoutView == 'map' &&
      parentMarketDetails &&
      submarkets != 'loading' &&
      submarkets &&
      submarkets.length > 0
    ) {
      setShouldShowSubmarketMarkers(true);
    } else {
      setShouldShowSubmarketMarkers(false);
    }
  }, [dashboardLayoutView, submarkets, parentMarketDetails]);

  const submarketMarkers = useMemo(() => {
    if (shouldShowSubmarketMarkers && bounds && Object.keys(bounds).length > 0) {
      let submarkers = submarkets.filter(submarket => {
        const googleMapsBounds = {
          sw: { lat: bounds.southWest.lat, lng: bounds.southWest.lng },
          ne: { lat: bounds.northEast.lat, lng: bounds.northEast.lng },
        };
        return (
          isPointWithinBounds(submarket.center, googleMapsBounds as GoogleMapReact.Bounds) &&
          submarket.submarketName !== search.data.name
        );
      });

      if (submarkers.length > 3) {
        submarkers = submarkers.slice(0, 3);
      }

      return submarkers.map((submarket: Submarket, index: number) => (
        <SubmarketPointer
          key={index + '-submarket-pointer'}
          lat={submarket.center.lat}
          lng={submarket.center.lng}
          submarket={submarket}
          currency={currency}
          userhasDataAccess={isUserAllowed}
          handleOnSampleClick={handleOnSampleClick}
        />
      ));
    }
    return <></>;
  }, [shouldShowSubmarketMarkers, submarkets, bounds]);

  const renderSubmarketPolygons = () => {
    if (!submarkets || !mapCopy || !mapsCopy) return;

    resetAllPolygons();
    searchPolygonCopy && searchPolygonCopy.setMap(null);

    const heatmapSubmarkets = visibleSubmarkets.map((neighbourhood, index) => {
      const wktBoundaryString = neighbourhood.boundary;
      const neighbourhoodCoordinates = getCoordinatesFromWKT(wktBoundaryString);
      const hexagonPath = neighbourhoodCoordinates.map(coord => ({
        lng: coord[0],
        lat: coord[1],
      }));
      return {
        ...neighbourhood,
        submarketId: neighbourhood.submarketId,
        path: [hexagonPath],
        occupancyRate: neighbourhood.occupancy,
        id: index,
        listingCount: neighbourhood.supply,
        revenue: neighbourhood.revenue,
      };
    });
    // set vhropleth graph breaks and assign colors to submarkets
    const submarketOccupancyRates = heatmapSubmarkets.map(submarket => submarket.occupancyRate);
    const validSubmarketOccupancyRates = submarketOccupancyRates.filter(
      rate => rate !== undefined && !Number.isNaN(rate)
    );

    const submarketRevenues = heatmapSubmarkets.map(submarket => submarket.revenue);
    const validSubmarketRevenues = submarketRevenues.filter(
      rate => rate !== undefined && !Number.isNaN(rate)
    );

    const submarketListingCounts = heatmapSubmarkets.map(submarket => submarket.listingCount);
    const validSubmarketListingsCounts = submarketListingCounts.filter(
      count => count !== undefined && !Number.isNaN(count)
    );

    let choroplethBreaks;
    if (neighbourhoodAnalysisKey === 'occupancy') {
      choroplethBreaks = getNaturalBreaks(validSubmarketOccupancyRates);
    }
    if (neighbourhoodAnalysisKey === 'listing') {
      choroplethBreaks = getNaturalBreaks(validSubmarketListingsCounts);
    }
    if (neighbourhoodAnalysisKey === 'revenue') {
      choroplethBreaks = getNaturalBreaks(validSubmarketRevenues);
    }

    const [unit, location] = getLegendUnit(neighbourhoodAnalysisKey, currency);

    const legends = {
      [`< ${formatLegendLabel(
        choroplethBreaks[0] + 1,
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[0],
      [`${formatLegendLabel(
        choroplethBreaks[0] + 1,
        choroplethBreaks[1],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[1],
      [`${formatLegendLabel(
        choroplethBreaks[1] + 1,
        choroplethBreaks[2],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[2],
      [`${formatLegendLabel(
        choroplethBreaks[2] + 1,
        choroplethBreaks[3],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[3],
      [`> ${formatLegendLabel(
        choroplethBreaks[3],
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[4],
    };
    setNeighbourhoodChoroplethLegends(legends);

    let submarketPolygons: any[] = [];

    heatmapSubmarkets.forEach((submarket, index) => {
      const submarketKey =
        neighbourhoodAnalysisKey === 'occupancy'
          ? submarket.occupancyRate
          : neighbourhoodAnalysisKey === 'revenue'
          ? submarket.revenue
          : submarket.listingCount;
      const color = getSubmarketColor(submarketKey, choroplethBreaks, neighbourhoodAnalysisKey);

      submarket.color = color;

      const isHovered = submarket.submarketId == hoveredSubmarketId;

      const zIndexValue = isHovered ? 10 : 5 + index * 0.01;

      const submarketPolygon = setPolygon(
        mapCopy,
        mapsCopy,
        submarket.path,
        COLOR_PRIMARY_DARK,
        color,
        0.8,
        isHovered ? 4 : 2,
        0.8,
        submarket.submarketId,
        submarket,
        zIndexValue
      );
      submarketPolygons.push(submarketPolygon);
    });

    setSubmarketPolygons(submarketPolygons);

    // Set the zoom level to the current zoom level of the map after applying bounds
    if (mapCopy) {
      const currentZoom = mapCopy.getZoom();
      if (currentZoom) {
        setZoom(currentZoom);
      }
    }
  };

  const resetAllPolygons = () => {
    submarketPolygons &&
      submarketPolygons.forEach(polygon => {
        polygon && polygon.setMap(null);
      });
    setSubmarketPolygons([]);

    hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
    setHoveredClusterPolygon(null);

    setHoveredClusterData(null);
    setHoveredSubmarketId(null);
    setHoveredSubmarketData(null);
    setTooltipPosition(null);
  };

  const resetSubmarketPolygons = () => {
    resetAllPolygons();
  };

  const callClusterListingsCoords = async submarket => {
    const clusterListingsCoords = await getClusterListingsCoords(submarket.coordinates);
    setClusterListingsCoords(clusterListingsCoords);
  };

  const handleClusterHover = submarket => {
    setMapLoading(true);
    callClusterListingsCoords(submarket);
    setHoveredClusterData(submarket);
    setTimeout(() => {
      setMapLoading(false);
    }, 500);
  };

  const handleClusterHoverOut = () => {
    setHoveredClusterData(null);
    setClusterListingsCoords([]);
  };

  const renderHeatmap = () => {
    if (!clusters || !mapCopy || !mapsCopy) return;

    resetAllPolygons();

    let heatmapSubmarkets;
    if (heatmapMode == 'smartZones') {
      heatmapSubmarkets = clusters.map((cluster, index) => {
        // Convert coordinates array to path format
        const hexagonPath = cluster.coordinates.map(coord => ({
          lng: coord[0],
          lat: coord[1],
        }));
        return {
          ...cluster,
          submarketId: cluster.cluster_id,
          path: [hexagonPath],
          occupancyRate: cluster.occupancyRate,
          listingCount: cluster.supply,
          revenue: cluster.revPar * cluster.supply,
          centroid: cluster.centroid,
          cluster_id: cluster.cluster_id,
        };
      });
    } else {
      heatmapSubmarkets = submarkets.map(submarket => {
        const wktBoundaryString = submarket.boundary;
        const neighbourhoodCoordinates = getCoordinatesFromWKT(wktBoundaryString);
        const hexagonPath = neighbourhoodCoordinates.map(coord => ({
          lng: coord[0],
          lat: coord[1],
        }));
        return {
          ...submarket,
          submarketId: submarket.submarketId,
          path: [hexagonPath],
          occupancyRate: submarket.occupancy,
          listingCount: submarket.supply,
          revenue: submarket.revenue,
          adr: submarket.adr,
          revPar: submarket.occupancyRate
            ? Math.round((submarket.adr * submarket.occupancyRate) / 100)
            : null,
          centroid: {
            latitude: submarket.center.lat,
            longitude: submarket.center.lng,
          },
          coordinates: neighbourhoodCoordinates,
        };
      });
    }

    // Calculate choropleth breaks based on selected metric
    const submarketOccupancyRates = heatmapSubmarkets.map(submarket => submarket.occupancyRate);
    const validSubmarketOccupancyRates = submarketOccupancyRates.filter(
      rate => rate !== undefined && !Number.isNaN(rate)
    );

    const submarketListingCounts = heatmapSubmarkets.map(submarket => submarket.listingCount);
    const validSubmarketListingsCounts = submarketListingCounts.filter(
      count => count !== undefined && !Number.isNaN(count)
    );
    const validSubmarketRevPar = heatmapSubmarkets.map(submarket => submarket.revPar);
    const validSubmarketADR = heatmapSubmarkets.map(submarket => submarket.adr);

    let choroplethBreaks;
    if (clusterKey === 'occupancyRate') {
      choroplethBreaks = getNaturalBreaks(validSubmarketOccupancyRates);
    } else if (clusterKey === 'supply') {
      choroplethBreaks = getNaturalBreaks(validSubmarketListingsCounts);
    } else if (clusterKey === 'revPar') {
      choroplethBreaks = getNaturalBreaks(validSubmarketRevPar, currency);
    } else if (clusterKey === 'adr') {
      choroplethBreaks = getNaturalBreaks(validSubmarketADR, currency);
    }

    const [unit, location] = getLegendUnit(clusterKey, currency);

    const legends = {
      [`< ${formatLegendLabel(
        choroplethBreaks[0] + 1,
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[0],
      [`${formatLegendLabel(
        choroplethBreaks[0] + 1,
        choroplethBreaks[1],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[1],
      [`${formatLegendLabel(
        choroplethBreaks[1] + 1,
        choroplethBreaks[2],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[2],
      [`${formatLegendLabel(
        choroplethBreaks[2] + 1,
        choroplethBreaks[3],
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[3],
      [`> ${formatLegendLabel(
        choroplethBreaks[3],
        undefined,
        unit,
        location
      )}`]: NEIGHBOURHOOD_THEME[4],
    };
    setNeighbourhoodChoroplethLegends(legends);

    let submarketPolygons: any[] = [];

    heatmapSubmarkets.forEach((submarket, index) => {
      const submarketKey =
        clusterKey === 'occupancyRate' ? submarket.occupancyRate : submarket.listingCount;
      const color = getSubmarketColor(submarketKey, choroplethBreaks, clusterKey);

      submarket.color = color;

      const zIndexValue = 5 + index * 0.01;

      const submarketPolygon = setPolygon(
        mapCopy,
        mapsCopy,
        submarket.path,
        heatmapMode == 'submarkets' ? COLOR_PRIMARY_2 : color,
        color,
        heatmapMode == 'submarkets' ? 0.8 : 0, // strokeOpacity
        heatmapMode == 'submarkets' ? 2 : 0, // strokeWeight
        shouldShowClusters ? 0.8 : 0.2, // fillOpacity
        undefined, // submarketId
        submarket, // submarketData
        zIndexValue // zIndex
      );

      // Add hover event listeners
      shouldShowClusters &&
        mapsCopy.event.addListener(submarketPolygon, 'mouseover', () => {
          submarketPolygon.setOptions({
            strokeWeight: 3,
            strokeOpacity: 0.8,
            strokeColor: COLOR_PRIMARY_2,
            fillOpacity: 0.2,
            zIndex: 10,
          });
          !matches && handleClusterHover(submarket);
        });

      shouldShowClusters &&
        mapsCopy.event.addListener(submarketPolygon, 'mouseout', () => {
          submarketPolygon.setOptions({
            strokeWeight: heatmapMode == 'submarkets' ? 2 : 0,
            strokeOpacity: heatmapMode == 'submarkets' ? 0.8 : 0,
            strokeColor: heatmapMode == 'submarkets' ? COLOR_PRIMARY_2 : color,
            fillOpacity: shouldShowClusters ? 0.8 : 0.2,
            zIndex: zIndexValue,
          });
          handleClusterHoverOut();
        });

      shouldShowClusters &&
        mapsCopy.event.addListener(submarketPolygon, 'click', e => {
          setHasClickedCluster(true);
          applySmartBoundsForCoords(mapCopy, mapsCopy, submarket.coordinates);
        });

      shouldShowClusters &&
        !matches &&
        mapsCopy.event.addListener(submarketPolygon, 'rightclick', e => {
          setRightClickedCluster(submarket);
          return false;
        });

      // not sure if this is working
      shouldShowClusters &&
        matches &&
        mapsCopy.event.addListener(submarketPolygon, 'contextmenu', e => {
          console.log('long clicked on polygon:', submarket);
          setRightClickedCluster(submarket);
          return false;
        });

      submarketPolygons.push(submarketPolygon);
    });

    setSubmarketPolygons(submarketPolygons);
  };

  useEffect(() => {
    if (dashboardLayoutView === 'submarkets') {
      // Reset any existing map state
      setMapCenter({ lat: search.data.lat, lng: search.data.lng });
      setZoom(12); // Set a default zoom level
      renderSubmarketPolygons();
    } else {
      resetSubmarketPolygons();
    }
  }, [dashboardLayoutView, visibleSubmarkets, mapCopy, mapsCopy, hoveredSubmarketId]);

  useEffect(() => {
    if (isTabChanging) return;

    // Store previous zoom to detect zoom out
    if (zoom !== null && lastZoomLevel !== null) {
      // If zooming out and we're at a level where clusters should show again
      if (zoom < lastZoomLevel && zoom < CLUSTER_BREAK_ZOOM_LEVEL) {
        setHasClickedCluster(false); // Reset the clicked cluster state on zoom out
      }
    }
    setLastZoomLevel(zoom);

    // Determine whether to show clusters or listings
    if (
      hasClickedCluster ||
      visibleMapClusters.length == 0 ||
      zoom >= CLUSTER_BREAK_ZOOM_LEVEL ||
      mapMode == 'listings'
    ) {
      setShouldShowClusters(false);
    } else {
      setShouldShowClusters(true);
    }
  }, [zoom, visibleMapClusters, mapMode, isTabChanging, hasClickedCluster, lastZoomLevel]);

  useEffect(() => {
    if (mapMode == 'heatmap' && clusters.length > 0) {
      renderHeatmap();
    } else {
      resetSubmarketPolygons();
    }
  }, [
    mapMode,
    clusters,
    clusterKey,
    mapCopy,
    mapsCopy,
    shouldShowClusters,
    dashboardLayoutView,
    currency,
    heatmapMode,
    matches,
  ]);

  useEffect(() => {
    if (mapMode != 'clusters') {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
      setHoveredClusterPolygon(null);
      return;
    }

    if (hoveredClusterData) {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);

      const hexagonPath = hoveredClusterData.coordinates.map(coord => ({
        lng: coord[0],
        lat: coord[1],
      }));
      // set polygon to be highlighted
      const polygon = setPolygon(
        mapCopy,
        mapsCopy,
        [hexagonPath],
        COLOR_PRIMARY_2,
        COLOR_PRIMARY_2,
        0,
        0,
        0.2
      );
      setHoveredClusterPolygon(polygon);
    } else {
      hoveredClusterPolygon && hoveredClusterPolygon.setMap(null);
      setHoveredClusterPolygon(null);
    }
  }, [mapMode, hoveredClusterData, mapCopy, mapsCopy]);

  useEffect(() => {
    if (clickedClusterId) {
      const cluster = clusters.find(cluster => cluster.cluster_id == clickedClusterId);
      if (!cluster) return;
      setHasClickedCluster(true);
      applySmartBoundsForCoords(mapCopy, mapsCopy, cluster.coordinates);
    }
  }, [clickedClusterId, clusters, mapCopy, mapsCopy]);

  return (
    <>
      {/* Top Left UI Group */}
      <Box
        sx={{
          position: 'absolute',
          top: matches ? '55px' : '25px',
          left: matches ? '15px' : '10px',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pointerEvents: 'none',
        }}
      >
        {/* Listing Count Info Box */}
        {isMarketTab && (
          <MapListingCountInfoBox
            open={isMarketTab}
            multipleMarkersCallLoading={multipleMarkersCallLoading}
            listingsLength={mapViewportListings.count}
            matches={matches}
          />
        )}

        {filterApplied && ['heatmap', 'clusters'].includes(mapMode) && (
          <Box
            sx={{
              background: '#0008',
              borderRadius: 4,
              padding: '5px 10px',
              backdropFilter: 'blur(4px)',
              height: '20px',
              overflow: 'hidden',
              pointerEvents: 'auto',
            }}
          >
            <Typography variant="body2" style={{ color: '#fff' }}>  
              Filter is not applied on cluster/heatmap.
            </Typography>
          </Box>
        )}

        {/* Zoom Buttons */}
        {matches && <ZoomButtons size={30} setZoom={setZoom} />}

        {/* All Filters Button */}
        {/* {matches && dashboardLayoutView == 'map' && (
          <Button
            className="mapFilterButton"
            size="small"
            onClick={() => toggleDrawer()}
            sx={{
              fontSize: '16',
              background: '#fff',
              color: COLOR_PRIMARY_DARK,
              pointerEvents: 'auto',
              width: '100px',
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
            All Filters
          </Button>
        )} */}
      </Box>

      {/* Top Right UI Group */}
      <Box
        sx={{
          position: 'absolute',
          top: matches ? '55px' : '25px',
          right: '15px',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {/* Close Button */}
        {matches && (
          <IconButton
            onClick={() => setDashboardLayoutView('dashboard')}
            sx={{
              cursor: 'pointer',
              background: '#fff',
              color: COLOR_RED,
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              pointerEvents: 'auto',
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {/* Map Mode Toggle */}
        {dashboardLayoutView == 'map' && (
          <Box
            sx={{
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
              borderRadius: '20px',
              pointerEvents: 'auto',
            }}
          >
            <Tooltip
              title={
                clusters.length == 0 && !useFullMarkers && !isUserAllowed
                  ? 'Subscribe to view in Heatmap / Cluster view'
                  : ''
              }
            >
              <ToggleButtonGroup
                color={'secondary'}
                value={mapMode}
                exclusive
                onChange={(event, newMapMode) => {
                  if (clusters.length == 0 && !useFullMarkers) {
                    setShowConfirmFullMarkersFetchModal(true);
                  }
                  setMapMode(newMapMode);
                }}
                aria-label="map mode"
                sx={{
                  backgroundColor: 'white',
                  width: 'auto',
                  height: 'auto',
                }}
                disabled={
                  clustersLoading || (clusters.length == 0 && !useFullMarkers && !isUserAllowed)
                }
              >
                <ToggleButton value="listings" aria-label="listings">
                  <Tooltip title="Listings">
                    <CottageIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="heatmap" aria-label="heatmap">
                  <Tooltip title="Heatmap">
                    <WhatshotIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="clusters" aria-label="clusters">
                  <Tooltip title="Cluster">
                    <BubbleChartIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>

            {clustersLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40px',
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress style={{ color: 'white' }} />
              </Box>
            )}
          </Box>
        )}

        {/* Heatmap Mode Dropdown - Moving from its current position */}
        {dashboardLayoutView == 'map' && mapMode == 'heatmap' && isClusterHeatmapActive && (
          <Box
            sx={{
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
              borderRadius: '20px',
              pointerEvents: 'auto',
            }}
          >
            <DropdownSelect2
              selectedKey={heatmapMode}
              options={heatmapModeOptions}
              setKey={setHeatmapMode}
              isDisabled={false}
            />
          </Box>
        )}

        {/* Metrics Dropdown - Moving from its current position */}
        {dashboardLayoutView == 'map' && isClusterHeatmapActive && mapMode != 'listings' && (
          <Box
            sx={{
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
              borderRadius: '20px',
              pointerEvents: 'auto',
            }}
          >
            <DropdownSelect2
              selectedKey={clusterKey}
              options={clusterMetricOptions}
              setKey={setClusterKey}
              isDisabled={!isUserAllowed}
              freeDataKey={'supply'}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Box>
        )}

        {/* Heatmap Legend Box */}
        {dashboardLayoutView == 'map' && isClusterHeatmapActive && mapMode != 'listings' && (
          <HeatmapLegendBox
            hideData={false}
            neighbourhoodAnalysisKey={clusterKey}
            neighbourhoodChoroplethLegends={
              mapMode == 'heatmap' ? neighbourhoodChoroplethLegends : clusterChoroplethLegends
            }
            loading={false}
            legendMap={clusterMetricOptions}
            defaultExpanded={matches ? false : true}
          />
        )}
      </Box>

      {/* Bottom Right UI Group */}
      {!matches && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '15px',
            right: '15px',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignItems: 'flex-end',
            pointerEvents: 'none',
          }}
        >
          <MapFilter
            search={search}
            toggleDrawer={toggleDrawer}
            setFilterVal={setFilterVal}
            applyFilter={applyFilter}
            filterVal={filterVal}
            filterActive={filterActive}
            setFilterActive={setFilterActive}
            isDisabled={isDisabled}
            handleOnSampleClick={handleOnSampleClick}
            listingsCount={listings.data ? Object.keys(listings.data).length : 0}
            setZoom={setZoom}
            onlyShowZoom={dashboardLayoutView !== 'map' || matches}
            onZoomInClick={() => {
              if (
                dashboardLayoutView !== 'map' &&
                dashboardLayoutView !== 'discovery' &&
                dashboardLayoutView !== 'compare' &&
                dashboardLayoutView !== 'submarkets'
              ) {
                setDashboardLayoutView('map');
                logAmplitudeEvent('map_viewed_in_overview', { cta: 'zoom_btn' });
              }
            }}
            dashboardLayoutView={dashboardLayoutView}
            noDataInMarket={noDataInMarket}
            bounds={bounds}
            callDynamicMarkers={callDynamicMarkers}
            marketID={marketID}
            showFilterComingSoonAlert={showFilterComingSoonAlert}
            setShowFilterComingSoonAlert={setShowFilterComingSoonAlert}
            filterCacheLoading={filterCacheLoading}
            listingSortingKey={listingSortingKey}
          />

          {dashboardLayoutView == 'map' && (
            <AppliedFiltersDisplay
              filterVal={filterVal}
              setFilterVal={setFilterVal}
              toggleDrawer={toggleDrawer}
              filterActive={filterActive}
              currency={currency}
              applyFilter={applyFilter}
            />
          )}
        </Box>
      )}
      {/* Main Google Map */}
      <GoogleMapReact
        bootstrapURLKeys={{
          key: GOOGLE_MAPS_API_KEY,
          libraries: ['drawing', 'places', 'visualization'],
          id: 'dashboard2',
        }}
        center={mapCenter}
        options={map => mapOptions(map, false)}
        zoom={zoom}
        resetBoundsOnResize={true}
        onChange={handleMapChange}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) =>
          apiIsLoaded(map, maps, search.data.polygon, search.data.multiPolygon)
        }
        onChildMouseEnter={(key, childProps) => {
          setHoveredClusterId(key as string);
        }}
        onChildMouseLeave={(key, childProps) => {
          setHoveredClusterId(null);
        }}
        onChildClick={(key, childProps) => {
          setClickedClusterId(key as string);
        }}
        {...(matches && { style: { height: collapsed ? '100vh' : `${100 - drawerHeight}vh` } })}
      >
        {/* cluster markers */}
        {!isTabChanging &&
          isMarketTab &&
          dashboardLayoutView !== 'submarkets' &&
          shouldShowClusters &&
          mapMode == 'clusters' &&
          clusterMarkers}

        {/* hovered cluster markers */}
        {!isTabChanging &&
          isMarketTab &&
          visibleMapListings &&
          dashboardLayoutView !== 'submarkets' &&
          shouldShowClusters &&
          hoveredClusterMarkers &&
          hoveredClusterMarkers}

        {/* markers */}
        {!isTabChanging &&
          isMarketTab &&
          visibleMapListings &&
          dashboardLayoutView !== 'submarkets' &&
          !shouldShowClusters &&
          markers}

        {/* discovery markers */}
        {!isTabChanging &&
          (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare') &&
          discoveryMarkers}

        {/* not selected discovery country pointer */}
        {notSelectedDiscoveryCountryPointer.length > 0 && notSelectedDiscoveryCountryPointer}

        {/* heatmap pointer */}
        {tooltipPosition && hoveredSubmarketData && dashboardLayoutView == 'submarkets' && (
          <HeatmapPointer
            lat={tooltipPosition.lat}
            lng={tooltipPosition.lng}
            pointerText={hoveredSubmarketData?.submarketName ?? null}
            metric={HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].metric}
            metricValue={
              hoveredSubmarketData[HEATMAP_METRIC_TO_DATA_KEY_MAP[neighbourhoodAnalysisKey].dataKey]
            }
            currency={currency}
            userhasDataAccess={isUserAllowed}
            handleOnSampleClick={handleOnSampleClick}
            scale={1}
          />
        )}

        {/* heatmap tooltip */}
        {hoveredClusterData && dashboardLayoutView != 'submarkets' && (
          <ClusterPointer
            marketID={search.data.id}
            lat={hoveredClusterData.centroid.latitude}
            lng={hoveredClusterData.centroid.longitude}
            supply={hoveredClusterData.supply}
            performanceMetrics={{
              adr: hoveredClusterData.adr,
              occupancyRate: hoveredClusterData.occupancyRate,
              revPar: hoveredClusterData.revPar,
              supply: hoveredClusterData.supply,
            }}
            clusterKey={clusterKey}
            currency={currency}
            color={COLOR_PRIMARY_2}
            shouldFade={false}
            savedClusters={savedClusters}
            setSavedClusters={setSavedClusters}
          />
        )}

        {/* clicked cluster save button */}
        {rightClickedCluster && dashboardLayoutView != 'submarkets' && (
          <SaveBoxPointer
            setClickedClusterData={setRightClickedCluster}
            lat={rightClickedCluster.centroid.latitude}
            lng={rightClickedCluster.centroid.longitude}
            currentClusterName={rightClickedCluster?.name}
            savedClusters={savedClusters}
            clusterId={rightClickedCluster?.cluster_id}
            performanceMetrics={{
              adr: rightClickedCluster?.adr,
              occupancyRate: rightClickedCluster?.occupancyRate,
              revPar: rightClickedCluster?.revPar,
              supply: rightClickedCluster?.supply,
            }}
            supply={rightClickedCluster?.supply}
            marketID={search.data.id}
            setSavedClusters={setSavedClusters}
            clusterKey={clusterKey}
          />
        )}

        {/* submarket markers */}
        {!isTabChanging && shouldShowSubmarketMarkers && submarketMarkers}
      </GoogleMapReact>

      {mapLoading && (
        <div className="loadingContainer">
          <div className="dotsContainer">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}

      {SearchBarModalComponent}

      {hideConfirmOpen && (
        <DecisionDialog
          open={hideConfirmOpen}
          handleClose={() => setHideConfirmOpen(false)}
          title="Hide Submarket"
          descriptions={['Are you sure you want to hide this submarket?']}
          agreeButtonText="Hide"
          disagreeButtonText="Cancel"
          onAgree={() => {
            handleHideSubmarket(selectedSubmarket.submarketId);
            setHideConfirmOpen(false);
          }}
          onDisagree={() => {
            setSelectedSubmarket(null);
            setHideConfirmOpen(false);
          }}
        />
      )}

      {dashboardLayoutView == 'submarkets' && (
        <HeatmapLegendBox
          hideData={false}
          neighbourhoodAnalysisKey={neighbourhoodAnalysisKey}
          neighbourhoodChoroplethLegends={neighbourhoodChoroplethLegends}
          loading={false}
          additionalStyle={{
            top: '80px',
          }}
        />
      )}

      {/* Replace the existing drawer with our CollapsibleDrawer */}
      {matches && isMarketTab && dashboardLayoutView === 'map' && (
        <CollapsibleDrawer
          drawerHeight={drawerHeight}
          setDrawerHeight={setDrawerHeight}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          listingsCount={activeListingCountDataToUse}
          onClose={() => setDashboardLayoutView('dashboard')}
          setMapHeightOnMobileView={setMapHeightOnMobileView}
          overlayButton={
            <DynamicDataUpdateButton
              onClick={() => callDynamicMarkers(marketID, bounds, filterVal)}
            />
          }
          minDrawerHeight={MIN_DRAWER_HEIGHT}
          maxDrawerHeight={MAX_DRAWER_HEIGHT}
          isOverLayVisible={isDynamicDataBtnVisible}
          components={{
            overview: [
              <Grid container key="overview-heading">
                <Grid item xs={10}>
                  {getSubSectionHeading(
                    getMapViewHeadings(
                      'Map Area Overview',
                      search.data ? marketDisplayName : '',
                      OverViewMapSectionMarketStatsTooltip
                    ),
                    'dashboard-overview-heading'
                  )}
                </Grid>
                <Grid item xs={2} justifyContent={'flex-end'}>
                  {!isDynamicDataBtnVisible && SaveSubMarketButton}
                </Grid>
              </Grid>,
              <>
                {warningTextComponent && warningTextComponent}
                MainKpiMapView(isMapCollapsed)
              </>,
              ,
              <Box sx={{ marginBottom: '50px' }} key="overview-spacing" />,
            ],
            listings: [
              ListingCountHeader,
              <ClickAwayListener
                onClickAway={() => {
                  setCurrentHoveredCardFromDataGrid(null);
                }}
              >
                {ListingCardGrid}
              </ClickAwayListener>,
            ],
          }}
        />
      )}

      {dashboardLayoutView == 'submarkets' && (
        <Box
          style={{
            width: '150px',
            padding: '10px 15px',
            background: 'white',
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
            position: 'absolute',
            top: '80px',
            left: '10px',
            zIndex: 3,
            transition: 'all 0.3s ease',
          }}
        >
          <ConfigSelect
            value={neighbourhoodAnalysisKey}
            setValue={setNeighbourhoodAnalysisKey}
            optionsMap={{
              revenue: 'Revenue',
              occupancy: 'Occupancy Rate',
              listing: 'Supply',
            }}
            activeColor={COLOR_PRIMARY_2}
            isDisabled={!isUserAllowed}
            freeDataKey={'listing'}
          />
        </Box>
      )}

      {/* Filter bar */}
      {matches && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            background: 'white',
            // zIndex: 9999
          }}
        >
          {FilterBarComponent}
        </Box>
      )}
    </>
  );
};

export default React.memo(Map);
